/**
 * Conversion between LayoutDocument (domain) and persistence payload.
 *
 * Used by web-client (API payloads) and Electron (local DB) without
 * duplicating layout logic. No Prisma or fetch here.
 */

import type {
  LayoutDocument,
  LayoutScene,
  UserLayoutThemeInfo,
} from "./types.js";
import { normalizeUserLayoutDisciplineKey } from "./types.js";
import { prepareLayoutDocumentForPersistence } from "./prune-dead-layout-list-row-variants.js";
import { stripRuntimeLayoutProps } from "./strip-runtime-layout-props.js";

/**
 * Payload shape for API and DB: flat fields compatible with UserLayout (remote)
 * and userLayouts (local). Scene is the same structure as LayoutScene.
 */
export interface UserLayoutPayload {
  id?: string;
  name: string;
  type: "widget" | "display";
  canvasWidth: number;
  canvasHeight: number;
  backgroundColor?: string | null;
  scene: LayoutScene;
  version?: number;
  themeId?: string | null;
  targetKey?: string | null;
  disciplineKey?: string | null;
  theme?: UserLayoutThemeInfo | null;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Builds a persistence payload from a LayoutDocument.
 *
 * @param doc - The layout document from the editor.
 * @returns Payload suitable for PATCH or for creating a new row (id optional).
 */
export function layoutToPayload(doc: LayoutDocument): UserLayoutPayload {
  const docForPersistence = prepareLayoutDocumentForPersistence(doc);
  return {
    id: docForPersistence.meta.id,
    name: docForPersistence.meta.name,
    type: docForPersistence.meta.type,
    canvasWidth: docForPersistence.meta.canvas.width,
    canvasHeight: docForPersistence.meta.canvas.height,
    backgroundColor: docForPersistence.scene.backgroundColor ?? null,
    scene: {
      children: stripRuntimeLayoutProps(docForPersistence.scene.children),
      backgroundColor: docForPersistence.scene.backgroundColor,
      catalog: docForPersistence.scene.catalog,
      nodeNameCounters: docForPersistence.scene.nodeNameCounters,
    },
    version: docForPersistence.version,
    disciplineKey: normalizeUserLayoutDisciplineKey(
      docForPersistence.meta.disciplineKey,
    ),
    createdAt: docForPersistence.meta.createdAt,
    updatedAt: docForPersistence.meta.updatedAt,
  };
}

/**
 * Builds a LayoutDocument from a persistence payload (e.g. API response).
 *
 * @param payload - The payload from GET /user-layouts/:id or local DB.
 * @returns Full LayoutDocument for the editor.
 */
export function payloadToLayoutDocument(
  payload: UserLayoutPayload,
): LayoutDocument {
  const now = new Date().toISOString();
  return {
    version: payload.version ?? 1,
    meta: {
      id: payload.id ?? crypto.randomUUID(),
      name: payload.name,
      type: payload.type,
      canvas: {
        width: payload.canvasWidth,
        height: payload.canvasHeight,
      },
      disciplineKey: normalizeUserLayoutDisciplineKey(payload.disciplineKey),
      createdAt: payload.createdAt ?? now,
      updatedAt: payload.updatedAt ?? now,
    },
    scene: {
      children: stripRuntimeLayoutProps(payload.scene?.children ?? []),
      backgroundColor:
        payload.backgroundColor ?? payload.scene?.backgroundColor ?? undefined,
      catalog: payload.scene?.catalog,
      nodeNameCounters: payload.scene?.nodeNameCounters,
    },
  };
}
